import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const isPlainObject = (value) =>
  value && typeof value === 'object' && !Array.isArray(value);

const humanize = (key) =>
  String(key)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (Array.isArray(value)) return value.map(formatValue).join(', ');
  if (isPlainObject(value)) return JSON.stringify(value);
  return String(value);
};

const flattenObject = (input, prefix = '') =>
  Object.entries(input || {}).reduce((acc, [key, value]) => {
    const nextKey = prefix ? `${prefix}.${key}` : key;
    if (isPlainObject(value)) {
      return { ...acc, ...flattenObject(value, nextKey) };
    }
    acc[nextKey] = value;
    return acc;
  }, {});

const findRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!isPlainObject(payload)) return [];

  const candidates = [
    payload.data,
    payload.result,
    payload.results,
    payload.records,
    payload.utilization,
    payload.usage,
    payload.items,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
    if (isPlainObject(candidate)) {
      const nestedRows = findRows(candidate);
      if (nestedRows.length) return nestedRows;
    }
  }

  return Object.keys(payload).length ? [payload] : [];
};

export default function UtilizationPage() {
  const { isAdmin, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [fromDate, setFromDate] = useState('2025-09-01');
  const [tillDate, setTillDate] = useState('2025-09-30');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;

    setLoadingUsers(true);
    api.get('/users')
      .then(({ data: res }) => {
        const activeUsers = (res.users || []).filter((u) => u.is_active !== false);
        setUsers(activeUsers);
        setSelectedUserId((current) => current || String(activeUsers[0]?.id || ''));
      })
      .catch(() => setError('Failed to load users'))
      .finally(() => setLoadingUsers(false));
  }, [isAdmin]);

  const fetchUtilization = useCallback(async () => {
    setLoading(true);
    setError('');
    setResponseMessage('');

    try {
      const body = { fromDate, tillDate };
      if (isAdmin && selectedUserId) body.userId = selectedUserId;

      const { data: res } = await api.post('/utilization/user', body);
      setData(res);

      const upstream = res.utilization || {};
      if (upstream.message) setResponseMessage(upstream.message);
    } catch (err) {
      setData(null);
      setError(err.response?.data?.error || err.response?.data?.upstream?.message || 'Failed to fetch utilization');
    } finally {
      setLoading(false);
    }
  }, [fromDate, isAdmin, selectedUserId, tillDate]);

  useEffect(() => {
    if (isAdmin && !selectedUserId) return;
    fetchUtilization();
  }, [fetchUtilization, isAdmin, selectedUserId]);

  const rows = useMemo(() => findRows(data?.utilization?.data ?? data?.utilization), [data]);
  const flatRows = useMemo(() => rows.map((row) => (
    isPlainObject(row) ? flattenObject(row) : { value: row }
  )), [rows]);
  const columns = useMemo(() => {
    const keys = new Set();
    flatRows.forEach((row) => Object.keys(row).forEach((key) => keys.add(key)));
    return Array.from(keys);
  }, [flatRows]);

  const selectedUser = data?.user || users.find((u) => String(u.id) === String(selectedUserId)) || user;
  const canFetch = fromDate && tillDate && (!isAdmin || selectedUserId);

  return (
    <div style={styles.layout}>
      <Sidebar />
      <main style={styles.main}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.pageTitle}>Usage Utilization</h1>
            <p style={styles.pageSubtitle}>Fetch utilization by user email from the ARACSP API</p>
          </div>
          <button style={styles.primaryBtn} onClick={fetchUtilization} disabled={loading || !canFetch}>
            {loading ? 'Fetching...' : 'Refresh'}
          </button>
        </div>

        <div style={styles.filterCard}>
          {isAdmin && (
            <div style={styles.field}>
              <label style={styles.label}>User</label>
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                style={styles.input}
                disabled={loadingUsers}
              >
                {loadingUsers && <option>Loading users...</option>}
                {!loadingUsers && users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                ))}
              </select>
            </div>
          )}

          <div style={styles.field}>
            <label style={styles.label}>From Date</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={styles.input} />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Till Date</label>
            <input type="date" value={tillDate} onChange={(e) => setTillDate(e.target.value)} style={styles.input} />
          </div>
        </div>

        {selectedUser?.email && (
          <div style={styles.summaryBar}>
            <div>
              <span style={styles.summaryLabel}>User Email</span>
              <span style={styles.summaryValue}>{selectedUser.email}</span>
            </div>
            <div>
              <span style={styles.summaryLabel}>Period</span>
              <span style={styles.summaryValue}>{fromDate} to {tillDate}</span>
            </div>
            {data?.fetchedAt && (
              <div>
                <span style={styles.summaryLabel}>Fetched</span>
                <span style={styles.summaryValue}>{new Date(data.fetchedAt).toLocaleString()}</span>
              </div>
            )}
          </div>
        )}

        {error && <div style={styles.errorBox}>{error}</div>}
        {responseMessage && <div style={styles.noticeBox}>{responseMessage}</div>}

        <div style={styles.tableCard}>
          <div style={styles.tableHeader}>
            <h3 style={styles.tableTitle}>Utilization Details</h3>
            <span style={styles.badge}>{flatRows.length} rows</span>
          </div>

          {loading ? (
            <div style={styles.emptyState}>Fetching utilization...</div>
          ) : !flatRows.length || !columns.length ? (
            <div style={styles.emptyState}>No utilization records found for the selected filters.</div>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {columns.map((column) => (
                      <th key={column} style={styles.th}>{humanize(column)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {flatRows.map((row, index) => (
                    <tr key={index}>
                      {columns.map((column) => (
                        <td key={column} style={styles.td}>{formatValue(row[column])}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

const styles = {
  layout: { display: 'flex', background: '#f5f7fa', minHeight: '100vh', fontFamily: "'Inter', 'Segoe UI', sans-serif" },
 main: { marginLeft:0, flex:1, padding:'12px 12px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  pageTitle: { margin: 0, color: '#0f172a', fontSize: 26, fontWeight: 700 },
  pageSubtitle: { margin: '4px 0 0', color: '#64748b', fontSize: 13 },
  primaryBtn: { background: '#2563eb', border: 'none', color: '#fff', borderRadius: 8, padding: '10px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  filterCard: { display: 'grid', gridTemplateColumns: 'minmax(260px, 2fr) 180px 180px', gap: 14, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' },
  input: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', color: '#0f172a', fontSize: 13, outline: 'none' },
  summaryBar: { display: 'flex', gap: 32, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: '14px 18px', marginBottom: 16 },
  summaryLabel: { display: 'block', color: '#64748b', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 },
  summaryValue: { color: '#1e293b', fontSize: 13, fontWeight: 600 },
  errorBox: { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 14 },
  noticeBox: { background: '#fff7ed', border: '1px solid #fed7aa', color: '#c2410c', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 14 },
  tableCard: { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' },
  tableHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 18px', borderBottom: '1px solid #f1f5f9' },
  tableTitle: { margin: 0, color: '#0f172a', fontSize: 16, fontWeight: 600 },
  badge: { background: '#f1f5f9', color: '#64748b', fontSize: 11, padding: '4px 10px', borderRadius: 20 },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', color: '#64748b', fontSize: 11, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', padding: '12px 16px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc', whiteSpace: 'nowrap' },
  td: { padding: '13px 16px', color: '#334155', fontSize: 13, borderBottom: '1px solid #f8fafc', verticalAlign: 'top', maxWidth: 340, wordBreak: 'break-word' },
  emptyState: { padding: 40, textAlign: 'center', color: '#64748b', fontSize: 14 },
};
